import type { FirebaseApp } from 'firebase/app';
import {
  CustomProvider,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { Capacitor } from '@capacitor/core';
import { FirebaseAppCheck } from '@capacitor-firebase/app-check';

declare global {
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
}

export async function initAppCheck(app: FirebaseApp): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    // Native debug tokens are available ONLY on the two deliberate dev paths:
    // the Vite dev server (live-reload) or `pnpm cap:ios:debugtoken` (builds
    // with --mode appcheck-debug, for the simulator). A plain `pnpm build` —
    // what cap:ios and the release archive use — can NEVER enable debug, no
    // matter what sits in .env.local. Release builds attest via DeviceCheck /
    // Play Integrity. (An env flag used to control this and had to be
    // hand-stripped before every archive; a forgotten flag would have shipped
    // a debug provider and 403'd every backend call in production.)
    const useNativeDebug = import.meta.env.DEV || import.meta.env.MODE === 'appcheck-debug';
    await FirebaseAppCheck.initialize({
      isTokenAutoRefreshEnabled: true,
      debugToken: useNativeDebug,
    });
    initializeAppCheck(app, {
      provider: new CustomProvider({
        getToken: async () => {
          const { token } = await FirebaseAppCheck.getToken();
          // The Capacitor plugin doesn't surface the native token's expiry, so
          // assume the App Check default of 1h. JS SDK auto-refresh will fetch
          // a fresh token before this elapses.
          return { token, expireTimeMillis: Date.now() + 3600 * 1000 };
        },
      }),
      isTokenAutoRefreshEnabled: true,
    });
    return;
  }

  // Web: only the Vite dev server uses a debug token. A built/deployed web app
  // (preview or Hosting) MUST use the real reCAPTCHA Enterprise provider — a
  // debug token is not registered for public visitors, so the SDK's
  // exchangeDebugToken call 403s and no App Check token is minted. We
  // deliberately do NOT honor VITE_APP_CHECK_DEBUG here, so the local dev flag
  // cannot leak into a production deploy.
  if (import.meta.env.DEV) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  const siteKey: string | undefined = import.meta.env.VITE_APP_CHECK_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    if (import.meta.env.DEV) {
      console.warn(
        '[appCheck] VITE_APP_CHECK_RECAPTCHA_SITE_KEY missing — App Check init skipped (DEV only).',
      );
      return;
    }
    throw new Error('VITE_APP_CHECK_RECAPTCHA_SITE_KEY is required in non-DEV builds');
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}
